import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Tooltip } from '@mui/material';
import DataTable from 'react-data-table-component';
import { Input } from 'reactstrap';
import { formatNumber } from 'src/utils/formatNumber';

const tableCustomStyles = {
  headCells: {
    style: {
      fontSize: '0.78rem',
      fontWeight: 700,
      textTransform: 'uppercase' as const,
      color: '#495057',
      backgroundColor: '#f8f9fa',
      paddingTop: '10px',
      paddingBottom: '10px',
    },
  },
  cells: {
    style: {
      fontSize: '0.875rem',
      paddingTop: '10px',
      paddingBottom: '10px',
    },
  },
  rows: {
    style: {
      minHeight: '44px',
      '&:hover': { backgroundColor: '#f5f8ff' },
    },
  },
};

export type ProductivityTableRow = {
  quantitySort: number;
  quantityRaw: number | undefined;
  unitName: string;
  globalIndex: number;
  original: any;
};

type ProductivityTableColumnsProps = {
  t: (key: string) => string;
  data: ProductivityTableRow[];
  showEditAction?: boolean;
  editableQuantities?: boolean;
  onEditProductivity?: (item: any, globalIndex: number) => void;
  onQuantityChange?: (globalIndex: number, value: number | undefined) => void;
};

const ProductivityTableColumns = ({
  t,
  data,
  showEditAction = false,
  editableQuantities = false,
  onEditProductivity,
  onQuantityChange,
}: ProductivityTableColumnsProps) => {
  const columns: any[] = [
    {
      name: t('productivity.quantity'),
      selector: (row: ProductivityTableRow) => row.quantitySort,
      cell: (row: ProductivityTableRow) =>
        editableQuantities ? (
          <Input
            type="number"
            step="0.01"
            min="0"
            className="form-control form-control-sm"
            style={{ maxWidth: 140 }}
            value={row.quantityRaw ?? ''}
            onChange={(e) => {
              const val = e.target.value;
              if (val === '') {
                onQuantityChange?.(row.globalIndex, undefined);
                return;
              }
              const n = parseFloat(val);
              onQuantityChange?.(row.globalIndex, Number.isNaN(n) ? undefined : n);
            }}
          />
        ) : (
          <>
            {row.quantityRaw !== undefined && row.quantityRaw !== null ? formatNumber(row.quantityRaw) : '-'}{' '}
            <span style={{ color: '#888', fontSize: '0.9em' }}>({row.unitName || '-'})</span>
          </>
        ),
      sortable: true,
      center: true,
      grow: 1.2,
    },
  ];

  if (showEditAction) {
    columns.push({
      id: 'Action',
      name: t('actions'),
      cell: (row: ProductivityTableRow) => (
        <Tooltip title={t('edit')}>
          <EditOutlinedIcon
            onClick={() => onEditProductivity?.(row.original, row.globalIndex)}
            sx={{
              cursor: 'pointer',
              color: '#2196f3',
              fontSize: 22,
              '&:hover': { color: '#1976d2' },
            }}
          />
        </Tooltip>
      ),
      sortable: false,
      center: true,
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    });
  }

  return <DataTable columns={columns} data={data} customStyles={tableCustomStyles} />;
};

export default ProductivityTableColumns;
